'use client';

import { paths } from '@/routes/paths';
import { seachForStore } from '@/services/stores';
import { useClickAway, useRequest } from 'ahooks';
import { SearchIcon } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';

type Props = {
  onSearchChanged: () => void;
};

export default function StoreSearch({ onSearchChanged }: Props) {
  const rootEl = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState('');
  const { data, error, run, cancel, loading } = useRequest(seachForStore, {
    manual: true,
    debounceWait: 500,
  });
  const router = useRouter();

  useClickAway(() => setQuery(''), rootEl);

  useEffect(() => {
    if (query) {
      onSearchChanged();
      run(query);
    }
  }, [query, run, onSearchChanged]);

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
  };

  const handleKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setQuery('');
      cancel();
    }
  };

  const handleSelect = (urlSlug: string) => {
    setQuery('');
    cancel();

    router.push(`${paths.promoCodes.root}/${urlSlug}`);
  };

  return (
    <div
      className="relative flex h-11 items-center rounded-full border border-slate-200"
      ref={rootEl}
    >
      <SearchIcon
        className="m-auto ml-3 shrink-0 rounded-full text-slate-400"
        size={22}
      />
      <input
        onKeyUp={handleKeyUp}
        value={query}
        onChange={handleChange}
        className="h-full w-full rounded-full border-none text-sm outline-hidden focus:ring-0 focus:outline-hidden"
        type="text"
        placeholder="Search for brands, e.g. Nike, Adidas"
      />

      {query && !loading && (
        <div className="absolute top-[calc(100%+15px)] w-full bg-white shadow-md">
          {error && <div>Error getting the list of stores</div>}
          {data?.values.length === 0 && !error && (
            <div className="p-3 text-center">No stores found</div>
          )}
          {data?.values.length && !error ? (
            <ul>
              {data.values.map((store) => (
                <li
                  key={store.urlSlug}
                  onClick={() => handleSelect(store.urlSlug || '')}
                  className="flex h-10 cursor-pointer items-center pl-4 align-middle hover:bg-slate-50"
                >
                  {store.name}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      )}
    </div>
  );
}
